import { useMemo, useRef } from "react";
import { Anchor } from "@mantine/core";
import { Link } from "react-router-dom";
import type { EcosystemHeadNavState } from "@/pages/ecosystem-heads/ecosystemHeads.const";
import EcosystemCorridorPlaceholderCard from "@/pages/ecosystem-heads/components/EcosystemCorridorPlaceholderCard";
import EcosystemCorridorPromoCard from "@/pages/ecosystem-heads/components/EcosystemCorridorPromoCard";
import { buildEcosystemCorridorEntries } from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import { resolvePlaceholderCorridorsForSector } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import type {
  SectorEcosystemCorridorHead,
  SectorEcosystemOverview,
} from "../../../../../../shared/sectorEcosystemOverview";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import SectorDashboardCard from "./SectorDashboardCard";
import SectorEcosystemCorridorCard from "./SectorEcosystemCorridorCard";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";

type SectorEcosystemHeadsCarouselProps = {
  sectorSlug: string;
  overview: SectorEcosystemOverview;
  headsHref: string;
  headsNavState?: EcosystemHeadNavState;
};

type CarouselCorridorItem =
  | { kind: "real"; corridor: SectorEcosystemCorridorHead }
  | { kind: "placeholder"; corridor: ComingSoonEcosystemCorridor };

const AUTOPLAY_DELAY_MS = 4500;

export default function SectorEcosystemHeadsCarousel({
  sectorSlug,
  overview,
  headsHref,
  headsNavState,
}: SectorEcosystemHeadsCarouselProps) {
  const realCorridors = overview.ecosystemHeadCorridors;
  const hasRealCorridors = realCorridors.length > 0;
  const placeholderCorridors = useMemo(
    () => resolvePlaceholderCorridorsForSector(sectorSlug, overview.title, hasRealCorridors),
    [sectorSlug, overview.title, hasRealCorridors],
  );

  const slides = useMemo(() => {
    const realItems: CarouselCorridorItem[] = realCorridors.map((corridor) => ({
      kind: "real" as const,
      corridor,
    }));
    const placeholderItems: CarouselCorridorItem[] = placeholderCorridors.map((corridor) => ({
      kind: "placeholder" as const,
      corridor,
    }));

    return buildEcosystemCorridorEntries(realItems, placeholderItems, (item) =>
      item.kind === "real"
        ? `real-${item.corridor.ecosystemHeadUserId}-${item.corridor.country}-${item.corridor.targetCountry}`
        : `placeholder-${item.corridor.id}`,
    );
  }, [realCorridors, placeholderCorridors]);

  const multi = slides.length > 1;

  const autoplay = useRef(
    Autoplay({
      delay: AUTOPLAY_DELAY_MS,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  return (
    <SectorDashboardCard
      fillHeight={false}
      title="Ecosystem corridor"
      subtitle="Leaders driving growth in this ecosystem."
      headerAction={
        slides.length > 0 ? (
          <Anchor component={Link} to={headsHref} state={headsNavState} size="xs" fw={600} c="primary.7">
            View all ecosystems →
          </Anchor>
        ) : undefined
      }
    >
      {slides.length === 0 ? null : (
        <Carousel
          slideSize={{ base: "85%", sm: "55%", md: "42%" }}
          slideGap="md"
          align="start"
          slidesToScroll={1}
          withControls={false}
          loop={multi}
          plugins={multi ? [autoplay.current] : []}
          onMouseEnter={multi ? autoplay.current.stop : undefined}
          onMouseLeave={multi ? autoplay.current.reset : undefined}
          styles={{
            slide: { height: "auto", minHeight: 188 },
          }}
        >
          {slides.map((entry) => {
            if (entry.type === "promo") {
              return (
                <Carousel.Slide key={entry.key}>
                  <EcosystemCorridorPromoCard />
                </Carousel.Slide>
              );
            }

            if (entry.item.kind === "real") {
              return (
                <Carousel.Slide key={entry.key}>
                  <SectorEcosystemCorridorCard corridor={entry.item.corridor} navState={headsNavState} />
                </Carousel.Slide>
              );
            }

            return (
              <Carousel.Slide key={entry.key}>
                <EcosystemCorridorPlaceholderCard corridor={entry.item.corridor} />
              </Carousel.Slide>
            );
          })}
        </Carousel>
      )}
    </SectorDashboardCard>
  );
}
